import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase-init';
import { useNotificationCounts } from '../contexts/NotificationCountsContext';
import {
  Calendar, ClipboardCheck, BarChart3, Briefcase,
  FileText, FileBarChart2, MessageSquare, Inbox, Settings, Scissors,
  ChevronLeft, ChevronRight, ChevronUp, LogOut, Archive,
  Globe, PenTool,
} from 'lucide-react';

const LOGO_URL = '/brand/twp-logo.png';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function Sidebar({ isCollapsed, onToggle }) {
  const { currentUser, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipY, setTooltipY] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) return;
    function handleOutside(e) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    }
    function handleEscape(e) {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountMenuOpen]);

  const role = userProfile?.role;
  const isOfficeManager = role === 'officeManager';
  const isNoah = currentUser?.email === 'nk@thewindowplacellc.com';

  const [pendingJobOrders, setPendingJobOrders] = useState(0);
  useEffect(() => {
    if (!isOfficeManager) return;
    const q = query(collection(db, 'jobOrders'), where('status', '==', 'pending_review'));
    const unsub = onSnapshot(q, snap => setPendingJobOrders(snap.size));
    return () => unsub();
  }, [isOfficeManager]);

  const { unreadSms } = useNotificationCounts();

  function badgeCount(path) {
    if (path === '/job-orders') return pendingJobOrders;
    if (path === '/communications') return unreadSms;
    return 0;
  }

  // Grouped under small section labels once there's more than a handful of
  // items — keeps the sidebar scannable. Every item here is open to every
  // signed-in role now except Job Orders (officeManager only) and Settings
  // (Noah only) — those two stay restricted, everything else was unhidden.
  const navGroups = [
    { label: 'Workflow', items: [
      { path: '/', icon: Calendar, label: 'Calendar' },
      { path: '/jobs', icon: Briefcase, label: 'Production' },
      { path: '/estimates', icon: FileText, label: 'Estimates' },
      ...(isOfficeManager ? [{ path: '/job-orders', icon: ClipboardCheck, label: 'Job Orders' }] : []),
      { path: '/cutlist', icon: Scissors, label: 'Cutlist Dashboard' },
    ] },
    { label: 'Team', items: [
      { path: '/archive', icon: Archive, label: 'Archive' },
      { path: '/communications', icon: MessageSquare, label: 'Communications' },
      { path: '/office-intake', icon: Inbox, label: 'Office Intake' },
    ] },
    { label: 'Admin', items: [
      { path: '/crm', icon: BarChart3, label: 'CRM Dashboard' },
      { path: '/reports', icon: FileBarChart2, label: 'Reports' },
      ...(isNoah ? [{ path: '/settings', icon: Settings, label: 'Settings' }] : []),
    ] },
    // The public website (replaces the old Window Studio link).
    { label: 'Website', items: [
      { path: '/website', icon: Globe, label: 'Website Dashboard', end: true },
      { path: '/website/design', icon: PenTool, label: 'Design Studio' },
    ] },
  ];

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const ROLE_LABELS = { officeManager: 'Office Manager', sales: 'Sales', admin: 'Admin', manager: 'Manager', installer: 'Installer', office: 'Office' };
  const roleLabel = ROLE_LABELS[role] || (role ? role.replace(/([a-z])([A-Z])/g, '$1 $2') : 'Manager');

  const getUserInitials = () => {
    const name = userProfile?.name || currentUser?.email || 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  const hoveredPath = navGroups.flatMap(g => g.items).find(i => i.label === hoveredItem)?.path;
  const hoveredCount = hoveredPath ? badgeCount(hoveredPath) : 0;

  function showTooltip(e, label) {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredItem(label);
    setTooltipY(rect.top + rect.height / 2);
  }
  function hideTooltip() {
    setHoveredItem(null);
  }

  function navItemStyle(isActive) {
    return {
      display: 'flex', alignItems: 'center',
      padding: isCollapsed ? '11px' : '10px 13px',
      marginBottom: '2px', borderRadius: '8px',
      textDecoration: 'none',
      color: isActive ? WALNUT : INK,
      background: isActive ? `${WALNUT}14` : 'transparent',
      borderLeft: isActive ? `3px solid ${WALNUT}` : '3px solid transparent',
      transition: 'all 0.15s ease',
      fontSize: '13.5px',
      fontFamily: FONT_SANS,
      fontWeight: isActive ? '600' : '500',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
      position: 'relative',
    };
  }

  const handleNavMouseEnter = (e) => {
    if (!e.currentTarget.classList.contains('active')) {
      e.currentTarget.style.background = `${WALNUT}0a`;
    }
  };
  const handleNavMouseLeave = (e) => {
    if (!e.currentTarget.classList.contains('active')) {
      e.currentTarget.style.background = 'transparent';
    }
  };

  return (
    <>
      <div style={{
        width: isCollapsed ? '80px' : '260px',
        height: '100vh',
        background: PAPER,
        borderRight: '1px solid rgba(139,69,19,0.1)',
        boxShadow: '2px 0 12px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0, top: 0,
        zIndex: 100,
        transition: 'width 0.3s ease',
        overflow: 'visible',
        fontFamily: FONT_SANS,
      }}>

        <button
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            position: 'absolute', top: '20px', right: '-14px',
            width: '28px', height: '28px', borderRadius: '50%',
            background: WALNUT,
            border: `2px solid ${PAPER}`,
            boxShadow: '0 2px 8px rgba(139,69,19,0.3)',
            color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.15s ease', zIndex: 101,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {isCollapsed ? <ChevronRight size={14} strokeWidth={2.5} /> : <ChevronLeft size={14} strokeWidth={2.5} />}
        </button>

        <div style={{
          padding: isCollapsed ? '20px 12px' : '20px',
          borderBottom: '1px solid rgba(139,69,19,0.08)',
          textAlign: 'center',
        }}>
          {!isCollapsed && (
            <>
              <img src={LOGO_URL} alt="TWP" style={{
                width: '56px', height: 'auto', marginBottom: '8px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                transition: 'transform 0.3s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
              <h2 style={{
                margin: 0, fontSize: '15px', fontWeight: 600, lineHeight: 1.25,
                fontFamily: FONT_DISPLAY, color: INK, letterSpacing: 0,
              }}>The Window Place LLC Hub</h2>
              <p style={{ margin: '2px 0 0 0', fontSize: '10.5px', color: STONE, fontWeight: '500' }}>
                Internal Operations
              </p>
            </>
          )}
          {isCollapsed && (
            <div style={{
              width: '38px', height: '38px',
              background: WALNUT,
              borderRadius: '10px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: 'white', fontWeight: '700',
              fontSize: '13px', margin: '0 auto',
            }}>TWP</div>
          )}
        </div>

        {!isCollapsed && (
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid rgba(139,69,19,0.08)',
            background: 'rgba(139,69,19,0.04)',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: WALNUT, marginBottom: '1px' }}>
              {formattedTime}
            </div>
            <div style={{ fontSize: '10.5px', color: STONE, fontWeight: '500' }}>
              {formattedDate}
            </div>
          </div>
        )}

        <nav style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
          {navGroups.map((group, gi) => (
            <div key={group.label || gi} style={{ marginBottom: gi < navGroups.length - 1 ? '14px' : 0 }}>
              {isCollapsed && gi > 0 && (
                <div style={{ height: '1px', background: 'rgba(139,69,19,0.1)', margin: '0 14px 12px' }} />
              )}
              {!isCollapsed && group.label && (
                <div style={{
                  fontSize: '10px', fontWeight: '700', color: STONE,
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  padding: '0 13px', marginBottom: '6px',
                }}>
                  {group.label}
                </div>
              )}
              {group.items.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  aria-label={isCollapsed ? item.label : undefined}
                  style={({ isActive }) => navItemStyle(isActive)}
                  onMouseEnter={e => { handleNavMouseEnter(e); showTooltip(e, item.label); }}
                  onMouseLeave={e => { handleNavMouseLeave(e); hideTooltip(); }}
                >
                  <span style={{ display: 'inline-flex', position: 'relative' }}>
                    <item.icon size={17} strokeWidth={2} />
                    {isCollapsed && badgeCount(item.path) > 0 && (
                      <span style={{
                        position: 'absolute', top: '-8px', right: '-12px',
                        background: RUST, color: 'white', borderRadius: '999px',
                        fontSize: '9.5px', fontWeight: '700', padding: '0 4px',
                        minWidth: '16px', height: '16px', lineHeight: '13px',
                        textAlign: 'center', boxSizing: 'border-box',
                        border: `1.5px solid ${PAPER}`,
                      }}>{badgeCount(item.path) > 99 ? '99+' : badgeCount(item.path)}</span>
                    )}
                  </span>
                  {!isCollapsed && (
                    <span style={{ marginLeft: '11px', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.path === '/job-orders' && pendingJobOrders > 0 && (
                        <span style={{
                          background: RUST, color: 'white', borderRadius: '999px',
                          fontSize: '10.5px', fontWeight: '700', padding: '1px 7px',
                          minWidth: '20px', textAlign: 'center', lineHeight: '17px',
                        }}>{pendingJobOrders}</span>
                      )}
                      {item.path === '/communications' && unreadSms > 0 && (
                        <span style={{
                          background: RUST, color: 'white', borderRadius: '999px',
                          fontSize: '10.5px', fontWeight: '700', padding: '1px 7px',
                          minWidth: '20px', textAlign: 'center', lineHeight: '17px',
                        }}>{unreadSms > 99 ? '99+' : unreadSms}</span>
                      )}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}

        </nav>

        <div ref={accountMenuRef} style={{
          padding: '10px 16px',
          borderTop: '1px solid rgba(139,69,19,0.08)',
          background: 'rgba(139,69,19,0.03)',
          position: 'relative',
        }}>
          {accountMenuOpen && (
            <div style={{
              position: 'absolute',
              ...(isCollapsed
                ? { left: 'calc(100% + 10px)', bottom: '10px', width: '220px' }
                : { left: '16px', right: '16px', bottom: 'calc(100% + 8px)' }),
              background: PAPER, borderRadius: '10px',
              border: '1px solid rgba(139,69,19,0.14)',
              boxShadow: '0 8px 24px rgba(43,38,32,0.16)',
              overflow: 'hidden', zIndex: 110,
            }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(139,69,19,0.08)' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userProfile?.name || currentUser?.email?.split('@')[0] || 'User'}
                </div>
                <div style={{ fontSize: '11px', color: STONE, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentUser?.email}
                </div>
              </div>
              <button
                onClick={handleSignOut}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 14px',
                  background: 'transparent', border: 'none',
                  color: RUST,
                  fontSize: '12.5px', fontWeight: '600', fontFamily: FONT_SANS,
                  cursor: 'pointer', transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${RUST}0f`; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={13} strokeWidth={2} /> Sign Out
              </button>
            </div>
          )}
          {!isCollapsed && (
            <button
              onClick={() => setAccountMenuOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '11px',
                padding: '6px', background: accountMenuOpen ? `${WALNUT}0f` : 'transparent',
                border: 'none', borderRadius: '8px', cursor: 'pointer',
                transition: 'background 0.15s ease', textAlign: 'left',
              }}
              onMouseEnter={e => { if (!accountMenuOpen) e.currentTarget.style.background = `${WALNUT}0a`; }}
              onMouseLeave={e => { if (!accountMenuOpen) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: WALNUT,
                color: 'white', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: '700', fontSize: '13px',
                flexShrink: 0,
              }}>
                {getUserInitials()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userProfile?.name || currentUser?.email?.split('@')[0] || 'User'}
                </div>
                <div style={{
                  display: 'inline-block', marginTop: '2px',
                  fontSize: '10px', fontWeight: '700', color: WALNUT,
                  background: `${WALNUT}14`, borderRadius: '5px', padding: '1px 6px',
                  textTransform: 'capitalize',
                }}>
                  {roleLabel}
                </div>
              </div>
              <ChevronUp size={14} strokeWidth={2.5} style={{
                color: STONE, flexShrink: 0,
                transform: accountMenuOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.15s ease',
              }} />
            </button>
          )}
          {isCollapsed && (
            <button
              onClick={() => { hideTooltip(); setAccountMenuOpen(o => !o); }}
              aria-label="Account menu"
              style={{
                border: 'none', padding: 0, fontFamily: FONT_SANS,
                width: '36px', height: '36px', borderRadius: '50%',
                background: WALNUT,
                color: 'white', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: '700', fontSize: '13px',
                margin: '0 auto', cursor: 'pointer',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; if (!accountMenuOpen) showTooltip(e, 'Account'); }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; hideTooltip(); }}
            >
              {getUserInitials()}
            </button>
          )}
        </div>
      </div>

      {isCollapsed && hoveredItem && (
        <div style={{
          position: 'fixed',
          left: '90px',
          top: tooltipY,
          transform: 'translateY(-50%)',
          background: INK,
          color: 'white',
          padding: '6px 12px',
          borderRadius: '7px',
          fontSize: '12px',
          fontWeight: '600',
          fontFamily: FONT_SANS,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <div style={{
            position: 'absolute', left: '-6px', top: '50%',
            transform: 'translateY(-50%)',
            width: 0, height: 0,
            borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent',
            borderRight: `6px solid ${INK}`,
          }} />
          {hoveredItem}
          {hoveredCount > 0 && (
            <span style={{
              background: RUST, borderRadius: '999px',
              fontSize: '10px', fontWeight: '700', padding: '1px 6px',
            }}>
              {hoveredCount > 99 ? '99+' : hoveredCount}
            </span>
          )}
        </div>
      )}
    </>
  );
}
